import { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Save } from "lucide-react";
import TipTapEditor from "@/components/editor/TipTapEditor";

const empty = () => ({
  heading_ar: "",
  heading_en: "",
  content_ar: "",
  content_en: "",
});

/** Migrate legacy `paragraphs_ar/en` arrays into HTML if `content_*` is empty. */
const migrate = (d = {}) => ({
  heading_ar: d.heading_ar || "",
  heading_en: d.heading_en || "",
  content_ar: d.content_ar || (Array.isArray(d.paragraphs_ar) ? d.paragraphs_ar.map((p) => `<p>${p}</p>`).join("") : ""),
  content_en: d.content_en || (Array.isArray(d.paragraphs_en) ? d.paragraphs_en.map((p) => `<p>${p}</p>`).join("") : ""),
});

const AdminAbout = () => {
  const [data, setData] = useState(empty());
  const [tab, setTab] = useState("ar");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/admin/content/about")
      .then((r) => setData(migrate(r.data?.data || {})))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/content/about", {
        heading_ar: data.heading_ar,
        heading_en: data.heading_en,
        content_ar: data.content_ar,
        content_en: data.content_en,
        paragraphs_ar: [],
        paragraphs_en: [],
      });
      toast.success("تم حفظ قسم النبذة");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="overline animate-pulse">…</div>;

  return (
    <div data-testid="admin-about">
      <Toaster position="top-left" theme="dark" richColors />
      <div className="overline mb-3">محتوى الصفحة</div>
      <h1 className="font-arabic font-black text-3xl sm:text-4xl text-bone mb-10">رحلتي في الإعلام</h1>

      <form onSubmit={save} className="space-y-10 max-w-5xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field
            label="العنوان (عربي)"
            value={data.heading_ar}
            onChange={(v) => setData((d) => ({ ...d, heading_ar: v }))}
            testId="about-heading-ar"
          />
          <Field
            label="Heading (English)"
            value={data.heading_en}
            onChange={(v) => setData((d) => ({ ...d, heading_en: v }))}
            testId="about-heading-en"
            ltr
          />
        </div>

        <div className="border border-white/10 bg-ink-soft">
          <div className="flex border-b border-white/10">
            <button
              type="button"
              onClick={() => setTab("ar")}
              className={`px-6 py-3 overline ${tab === "ar" ? "text-gold border-b-2 border-gold" : "text-white/60"}`}
              data-testid="about-tab-ar"
            >
              العربية
            </button>
            <button
              type="button"
              onClick={() => setTab("en")}
              className={`px-6 py-3 overline ${tab === "en" ? "text-gold border-b-2 border-gold" : "text-white/60"}`}
              data-testid="about-tab-en"
            >
              English
            </button>
          </div>
          <div className="p-5">
            {tab === "ar" ? (
              <TipTapEditor
                key="ar"
                value={data.content_ar}
                onChange={(html) => setData((d) => ({ ...d, content_ar: html }))}
                dir="rtl"
                placeholder="ابدأ بكتابة قصتك في الإعلام…"
              />
            ) : (
              <TipTapEditor
                key="en"
                value={data.content_en}
                onChange={(html) => setData((d) => ({ ...d, content_en: html }))}
                dir="ltr"
                placeholder="Tell your media story…"
              />
            )}
          </div>
        </div>

        <button type="submit" disabled={saving} className="btn-gold disabled:opacity-60" data-testid="about-save">
          <Save size={14} />
          {saving ? "جاري الحفظ…" : "حفظ"}
        </button>
      </form>
    </div>
  );
};

const Field = ({ label, value, onChange, testId, ltr }) => (
  <label className="block">
    <span className="block mb-2 text-xs tracking-[0.2em] uppercase text-white/50 font-arabic">{label}</span>
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="editorial-input"
      data-testid={testId}
      dir={ltr ? "ltr" : "auto"}
    />
  </label>
);

export default AdminAbout;
